import {
  DEFAULT_ORGANIZATION_CATEGORY,
  ORGANIZATION_CATEGORY,
  Organization,
  SupportedOrganizationCategory,
} from '@app/types/organization';
import { getOrganizationCategory } from '@app/utils/organization';

export const PRODUCT_WORKPLACE_INPUT = {
  MANUAL: 'manual',
  ORGANIZATION: 'organization',
} as const;

export const PRODUCT_WORK_LOCATION_INPUT = {
  MANUAL: 'manual',
  ORGANIZATION_OR_MANUAL: 'organizationOrManual',
} as const;

export const PRODUCT_WORK_LOCATION_DETAIL = {
  AREA_ONLY: 'areaOnly',
  FULL: 'full',
} as const;

interface ProductOrganizationPolicy {
  workLocationDetail: (typeof PRODUCT_WORK_LOCATION_DETAIL)[keyof typeof PRODUCT_WORK_LOCATION_DETAIL];
  workLocationInput: (typeof PRODUCT_WORK_LOCATION_INPUT)[keyof typeof PRODUCT_WORK_LOCATION_INPUT];
  workplaceInput: (typeof PRODUCT_WORKPLACE_INPUT)[keyof typeof PRODUCT_WORKPLACE_INPUT];
}

/** 組織区分ごとの求人勤務先・勤務地ルールの唯一の定義元 */
export const PRODUCT_ORGANIZATION_POLICY: Record<
  SupportedOrganizationCategory,
  ProductOrganizationPolicy
> = {
  [ORGANIZATION_CATEGORY.CLINIC]: {
    workLocationDetail: PRODUCT_WORK_LOCATION_DETAIL.FULL,
    workLocationInput: PRODUCT_WORK_LOCATION_INPUT.ORGANIZATION_OR_MANUAL,
    workplaceInput: PRODUCT_WORKPLACE_INPUT.ORGANIZATION,
  },
  [ORGANIZATION_CATEGORY.AGENCY]: {
    workLocationDetail: PRODUCT_WORK_LOCATION_DETAIL.AREA_ONLY,
    workLocationInput: PRODUCT_WORK_LOCATION_INPUT.MANUAL,
    workplaceInput: PRODUCT_WORKPLACE_INPUT.MANUAL,
  },
};

export function getProductOrganizationPolicy(
  organization?: Partial<Organization>
): ProductOrganizationPolicy {
  const category = getOrganizationCategory(organization);
  return (
    PRODUCT_ORGANIZATION_POLICY[category] ??
    PRODUCT_ORGANIZATION_POLICY[DEFAULT_ORGANIZATION_CATEGORY]
  );
}

/** 求人に掲載する勤務先名。人材紹介企業は求人ごとの手入力値を使用する */
export function getProductWorkplaceName(
  manualWorkplaceName: string,
  organization?: Partial<Organization>
): string {
  return isManualWorkplaceInput(organization)
    ? manualWorkplaceName
    : organization?.name ?? '';
}

/** 勤務地の町名・番地/建物名/アクセス情報を掲載するかどうか */
export function isFullWorkLocationDetail(
  organization?: Partial<Organization>
): boolean {
  return (
    getProductOrganizationPolicy(organization).workLocationDetail ===
    PRODUCT_WORK_LOCATION_DETAIL.FULL
  );
}

/** 勤務地(都道府県・市区町村など)を組織の住所から引き継がず、必ず手入力させるかどうか */
export function isManualWorkLocationInput(
  organization?: Partial<Organization>
): boolean {
  return (
    getProductOrganizationPolicy(organization).workLocationInput ===
    PRODUCT_WORK_LOCATION_INPUT.MANUAL
  );
}

/** 勤務先名を自組織名から引き継がず、求人ごとに手入力させるかどうか */
export function isManualWorkplaceInput(
  organization?: Partial<Organization>
): boolean {
  return (
    getProductOrganizationPolicy(organization).workplaceInput ===
    PRODUCT_WORKPLACE_INPUT.MANUAL
  );
}
